// Hotspots.tsx - Main component with fixed TypeScript typing for Storyblok
import React, { useCallback } from "react";
import { StoryblokBlockComponent } from "../../lib/storyblok/types/StoryblokComponent";
import { HotspotsBlock } from "../../lib/storyblok/types/blocks/HotspotsBlock";
import Button from "../../ui/Button";
import { useLinks } from "../../lib/storyblok/useLinks";
import HotspotsUi from "../../ui/Hotspots";
import StoryblokImage from "../StoryblokImage";
import { useContentStyle, useButtonStyle } from "../../hooks/useStyle";
import { isValidImageData, imageSizes } from "../../lib/utils/imageUtils";

const Hotspots: StoryblokBlockComponent<HotspotsBlock> = ({
  title,
  description,
  buttons,
  innerSpacing,
  outerSpacing,
  style,
  image,
  items,
}) => {
  const isIntroInverted = useContentStyle(style);
  const buttonIntroVariant = useButtonStyle(style);
  const resolveLink = useLinks();

  const renderIntroButtons = useCallback(
    () =>
      buttons?.map((button) => (
        <Button
          key={button._uid}
          variant={button.buttonType || buttonIntroVariant || "default"}
          href={button.buttonLink ? resolveLink(button.buttonLink) : ""}
        >
          {button.buttonText}
        </Button>
      )),
    [buttons, buttonIntroVariant, resolveLink]
  );

  return (
    <HotspotsUi
      title={title}
      description={description}
      buttons={renderIntroButtons()}
      style={style}
      innerSpacing={innerSpacing}
      outerSpacing={outerSpacing}
      inverted={isIntroInverted}
      image={
        image &&
        isValidImageData(image) && (
          <StoryblokImage
            image={image}
            sizes={imageSizes("100vw", {
              desktop: "1200px",
            })}
            resize={{ width: 1200 }}
            alt={title}
          />
        )
      }
    >
      {items?.map((item, index) => (
        <HotspotsUi.Item
          key={item._uid}
          _uid={item._uid}
          title={item.title}
          index={index + 1}
          description={item.description}
          style={item.style}
          coordinateX={Number(item.coordinateX?.value)}
          coordinateY={Number(item.coordinateY?.value)}
        />
      ))}
    </HotspotsUi>
  );
};

export default Hotspots;
